import React from 'react';
import * as XLSX from 'xlsx';
import { Button } from "antd";

const ExportFile = ({ payload, response, unit, ProductIncluded}) => {
  const handleDownload = () => {
    const nameAndVersion = `ECoTRIP_export_data_${process.env.REACT_APP_VERSION}.xlsx`;
    const responseSheetData = convertToSheetData(response, unit);
    const wb = XLSX.utils.book_new();
    const wsResponse = XLSX.utils.aoa_to_sheet(responseSheetData);
    XLSX.utils.book_append_sheet(wb, wsResponse, "Output Data");
    XLSX.writeFile(wb, nameAndVersion);
  };

  const convertPayloadToSheetData = (obj) => {
    const sheetData = [];
    const transientData = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        transientData[key] = value;
      } else {
        sheetData.push([key, value]);
      }
    });
    if (Object.keys(transientData).length > 0) {
      sheetData.push([]);
      sheetData.push(['Transient Data Tables']);
      sheetData.push([]);
    }
    Object.keys(transientData).forEach(key => {
      sheetData.push([key]);
      const items = transientData[key];
      if (items.length > 0) {
        const headers = Object.keys(items[0]);
        sheetData.push(headers);
        items.forEach(item => {
          const row = headers.map(header => item[header]);
          sheetData.push(row);
        });
        sheetData.push([]);
      }
    });
    return sheetData;
  };
  const convertM3sToLh = (m3s) => m3s * 3600 * 1000; // m³/s to L/h
  const convertM3sToGalh = (m3s) => m3s * 3600 * 264.172; // m³/s to gal/h
  const convertKelvinToCelsius = (kelvin) => kelvin - 273.15;
  const convertKelvinToFahrenheit = (kelvin) => (kelvin - 273.15) * 9 / 5 + 32;
  const convertM3ToGal = (m3) => m3 * 264.172; // m³ to gal
  const convertM3ToL = (m3) => m3 * 1000; // m³ to L
  const convertSecondsToHours = (seconds) => seconds / 3600; // seconds to hours

  const convertToSheetData = (obj, unit) => {
    const specificVariables = [
      'time', 'summary.E', 'summary.E_rate_avg', 'summary.lowSpeedRunTimeRatio',
      'summary.heatingOnRunTimeRatio', 'summary.coolingOnRunTimeRatio', 'summary.T_sensor[1]',
      'weather.T_ambient', 'plant.engine.N', 'summary.T_zone[1]', 'summary.P',
      'summary.highSpeedRunTimeRatio', 'summary.Q_flow[1]', 'summary.T_sensor[1]_RA', 
      'box.zone1.innerWallTemperatureSensor.T', 'box.zone1.outerWallTemperatureSensor.T',
      'plant.engine.torqueIn','plant.engine.P_load', 'plant.m_flowAir[1]'
    ];
    if (ProductIncluded === 'Yes') {
      specificVariables.push('box.zone1.T_product');
    }

    const customHeadings = {
      'time': 'Time(h)',
      'summary.E': `Fuel Consumption (${unit === 'IP' ? 'gal' : 'L'})`,
      'summary.E_rate_avg': `Average Fuel Rate (${unit === 'IP' ? 'gal/h' : 'L/h'})`,
      // 'summary.coolingOnRunTimeRatio': '% Engine On',
      'summary.lowSpeedRunTimeRatio': 'Percent Low Speed (%)',
      'summary.heatingOnRunTimeRatio': 'Percent Of Time In Heating (%)',
      'summary.coolingOnRunTimeRatio': 'Percent Of Time In Cooling (%)',
      'summary.T_sensor[1]': `Return Air Temp (${unit === 'IP' ? '°F' : '°C'})`,
      'weather.T_ambient': `Ambient Temperature (${unit === 'IP' ? '°F' : '°C'})`,
      'plant.engine.N': 'Engine Speed (rpm)',
      'summary.T_zone[1]': `Box Air Temp (${unit === 'IP' ? '°F' : '°C'})`,
      'summary.P': `Instant Fuel Rate (${unit === 'IP' ? 'gal/h' : 'L/h'})`,
      'summary.highSpeedRunTimeRatio': 'Percent High Speed (%)',
      'summary.Q_flow[1]': 'Net Capacity (W)',
      'box.zone1.innerWallTemperatureSensor.T':`Internal Wall Temp (${unit === 'IP' ? '°F' : '°C'})`,
      'box.zone1.outerWallTemperatureSensor.T':`External Wall Temp (${unit === 'IP' ? '°F' : '°C'})`,
      'plant.engine.torqueIn':'Torque (N-m)',
      'plant.engine.P_load':'Engine Power (W)',
      'plant.m_flowAir[1]':'Airflow (kg/s)',
      'box.zone1.T_product':`Product Temp (${unit === 'IP' ? '°F' : '°C'})`
      // 'summary.T_sensor[1]_RA': 'Sensor RA'
    };
   
    let headers = [];
    const rows = [];

    // Collect all keys first
    Object.keys(obj).forEach(key => {
      if (specificVariables.includes(key) && !headers.includes(key)) headers.push(key);
    });

    // Ensure 'time' is the first column
    if (headers.includes('time')) {
      headers = ['time', ...headers.filter(header => header !== 'time')];
    }

    // Replace 'summary.' with '' in headers and use custom headings
    headers = headers.map(header => {
      if (header.toLowerCase().includes('summary.')) {
        return customHeadings[header] || header.split('summary.')[1];
      }
      return customHeadings[header] || header;
    });

    // Populate rows
    Object.keys(obj).forEach(key => {
      if (!specificVariables.includes(key)) return;
      let value = obj[key];
      if (key === 'time') {
        value = Array.isArray(value) ? value.map(convertSecondsToHours) : convertSecondsToHours(value);
      }
      if (key === 'summary.E') {
        if (unit === 'IP') {
          value = Array.isArray(value) ? value.map(convertM3ToGal) : convertM3ToGal(value);
        } else {
          value = Array.isArray(value) ? value.map(convertM3ToL) : convertM3ToL(value);
        }
      }
      if (key === 'summary.P' || key === 'summary.E_rate_avg') {
        if (unit === 'IP') {
          value = Array.isArray(value) ? value.map(convertM3sToGalh) : convertM3sToGalh(value);
        } else {
          value = Array.isArray(value) ? value.map(convertM3sToLh) : convertM3sToLh(value);
        }
      }
      if (key === 'weather.T_ambient' || key === 'summary.T_zone[1]' || key === 'box.zone1.innerWallTemperatureSensor.T'|| 
        key === 'box.zone1.outerWallTemperatureSensor.T'|| key === 'box.zone1.T_product' || key =='summary.T_sensor[1]') {
        if (unit === 'IP') {
          value = Array.isArray(value) ? value.map(convertKelvinToFahrenheit) : convertKelvinToFahrenheit(value);
        } else {
          value = Array.isArray(value) ? value.map(convertKelvinToCelsius) : convertKelvinToCelsius(value);
        }
      }
      if (key === 'summary.highSpeedRunTimeRatio' || key === 'summary.lowSpeedRunTimeRatio' ||
        key === 'summary.heatingOnRunTimeRatio' || key === 'summary.coolingOnRunTimeRatio') {
        value = Array.isArray(value) ? value.map(v => v * 100) : value * 100;
      }
      let modifiedKey = key;
      if (key.toLowerCase().includes('summary.')) {
        modifiedKey = key.split('summary.')[1];
      }
      if (!headers.includes(customHeadings[key] || modifiedKey)) return;
      const colIndex = headers.indexOf(customHeadings[key] || modifiedKey);
      if (Array.isArray(value)) {
        value.forEach((item, i) => {
          if (Array.isArray(item)) {
            item.forEach((subItem, j) => {
              if (!rows[i * item.length + j]) rows[i * item.length + j] = [];
              rows[i * item.length + j][colIndex] = subItem;
            });
          } else {
            if (!rows[i]) rows[i] = [];
            rows[i][colIndex] = item;
          }
        });
      } else {
        if (!rows[0]) rows[0] = [];
        rows[0][colIndex] = value;
      }
    });

    // Add the second context for 'summary.T_sensor[1]' as 'Sensor RA'
    if (obj['summary.T_sensor[1]']) {
      const sensorRAValues = obj['summary.T_sensor[1]'];
      const sensorRAColIndex = headers.indexOf(`Sensor RA (${unit === 'IP' ? '°F' : '°C'})`);
      if (sensorRAColIndex !== -1) {
        if (Array.isArray(sensorRAValues)) {
          sensorRAValues.forEach((value, i) => {
            if (unit === 'IP') {
              value = convertKelvinToFahrenheit(value);
            } else {
              value = convertKelvinToCelsius(value);
            }
            if (!rows[i]) rows[i] = [];
            rows[i][sensorRAColIndex] = value;
          });
        } else {
          let value = sensorRAValues;
          if (unit === 'IP') {
            value = convertKelvinToFahrenheit(value);
          } else {
            value = convertKelvinToCelsius(value);
          }
          if (!rows[0]) rows[0] = [];
          rows[0][sensorRAColIndex] = value;
        }
      }
    }

    // Make headers bold
    const boldHeaders = headers.map(header => ({ v: header, s: { font: { bold: true } } }));
    rows.unshift(boldHeaders);
    headers.push('Engine On (%)');
    rows[0].push({ v: 'Engine On (%)', s: { font: { bold: true } } });

    const coolingOnValues = obj['summary.coolingOnRunTimeRatio'];
    const heatingOnValues = obj['summary.heatingOnRunTimeRatio'];

    if (coolingOnValues && heatingOnValues) {
      if (Array.isArray(coolingOnValues) && Array.isArray(heatingOnValues)) {
        coolingOnValues.forEach((coolingValue, i) => {
          let heatingValue = heatingOnValues[i];
          let engineOnValue = (coolingValue + heatingValue)* 100;
          if (!rows[i + 1]) rows[i + 1] = [];
          rows[i + 1][headers.length - 1] = engineOnValue;
        });
      } else {
        let engineOnValue = coolingOnValues + heatingOnValues;
        if (!rows[1]) rows[1] = [];
        rows[1][headers.length - 1] = engineOnValue;
      }
    }
  
   // Add the "Sensor RA" column at the end
   headers.push(`Sensor RA (${unit === 'IP' ? '°F' : '°C'})`);
   rows[0].push({ v: `Sensor RA (${unit === 'IP' ? '°F' : '°C'})`, s: { font: { bold: true } } });
   const sensorRAValues = obj['summary.T_sensor[1]'];
    if (sensorRAValues) {
      if (Array.isArray(sensorRAValues)) {
        sensorRAValues.forEach((value, i) => {
          if (unit === 'IP') {
            value = convertKelvinToFahrenheit(value);
          } else {
            value = convertKelvinToCelsius(value);
          }
          if (!rows[i + 1]) rows[i + 1] = [];
          rows[i + 1][headers.length - 1] = value; // Ensure the value is placed in the correct column
        });
      } else {
        let value = sensorRAValues;
        if (unit === 'IP') {
          value = convertKelvinToFahrenheit(value);
        } else {
          value = convertKelvinToCelsius(value);
        }
        if (!rows[1]) rows[1] = [];
        rows[1][headers.length - 1] = value; // Ensure the value is placed in the correct column
      }
    }

    return rows;
  };

  return (
    <Button
      type="primary"
      block
      size="large"
      onClick={handleDownload}
      style={{ width: '140px' }}
    >
      Download XLSX
    </Button>
  );
};

export default ExportFile;